import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SUPPORTED = ["nl", "de", "en"];
const SITE_URL = process.env.REACT_APP_SITE_URL || "";

// Strip existing /de or /en prefix from path, always return path starting with "/".
const stripLangPrefix = (pathname) => {
  const m = pathname.match(/^\/(de|en)(\/.*)?$/);
  if (m) return m[2] || "/";
  return pathname || "/";
};

// Build canonical URL for a given lang + base path (without prefix).
const buildUrl = (basePath, lang) => {
  const prefix = lang === "nl" ? "" : `/${lang}`;
  const path = basePath === "/" ? "" : basePath;
  return `${SITE_URL}${prefix}${path || "/"}`;
};

export const Seo = ({ title, description, lang = "nl", image, jsonLd, noindex = false }) => {
  const location = useLocation();
  const basePath = stripLangPrefix(location.pathname);
  const canonical = buildUrl(basePath, lang);
  const ogImage = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}/media/lemmer-watersport.jpg`;
  const ldBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // noindex-pagina's (bv. /leden): alleen titel + robots-meta, geen
  // canonical/hreflang/og — de pagina mag nergens vindbaar zijn.
  if (noindex) {
    return (
      <Helmet htmlAttributes={{ lang }}>
        {title && <title>{title}</title>}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  return (
    <Helmet htmlAttributes={{ lang }}>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {SITE_URL && <link rel="canonical" href={canonical} />}
      {SITE_URL &&
        SUPPORTED.map((l) => (
          <link
            key={l}
            rel="alternate"
            hrefLang={l}
            href={buildUrl(basePath, l)}
          />
        ))}
      {SITE_URL && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={buildUrl(basePath, "nl")}
        />
      )}
      <meta property="og:title" content={title || ""} />
      <meta property="og:description" content={description || ""} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === "nl" ? "nl_NL" : lang === "de" ? "de_DE" : "en_GB"} />
      {SITE_URL && <meta property="og:url" content={canonical} />}
      {SITE_URL && <meta property="og:image" content={ogImage} />}
      {SITE_URL && <meta name="twitter:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || ""} />
      <meta name="twitter:description" content={description || ""} />
      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
